import React, { useEffect, useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { Mail, Lock, Eye, EyeOff, LogIn, AlertCircle, Loader2 } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Button } from '../components/ui/button';
import { useAuth } from '../context/AuthContext';
import Header from '../components/Header';
import Footer from '../components/Footer';
import { MagicLinkAnfordern } from './MagicLinkLogin';
import { UpdateHinweis } from '../components/UpdateHinweis';

const Login = () => {
  const { t } = useTranslation();
  const { login, verify2fa } = useAuth();
  const [pending, setPending] = useState(() => window.history.state?.usr?.pending2fa || null);
  const [otp, setOtp] = useState('');
  const [merken, setMerken] = useState(false);
  const navigate = useNavigate();
  const location = useLocation();
  // Support both /login?next=/path and navigate(..., { state: { from } })
  const params = new URLSearchParams(location.search);
  const nextParam = params.get('next');
  if (params.get('code')) localStorage.setItem('kp_zugangscode', params.get('code').toUpperCase());
  const from = nextParam || location.state?.from || '/dashboard';
  const ziel = (u) => nextParam || location.state?.from || (['admin', 'kanzlei_mitarbeiter', 'freelancer'].includes(u?.role) ? '/kanzlei/eingang' : '/dashboard');

  const [form, setForm] = useState({ email: '', password: '' });
  const [showPw, setShowPw] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(() => {
    if (params.get('error') !== 'oauth_failed') return '';
    let detail = '';
    try { detail = sessionStorage.getItem('oauth_error') || ''; sessionStorage.removeItem('oauth_error'); } catch (err) { /* ignore */ }
    return detail || t('login.error_oauth');
  });
  const [googleLoading, setGoogleLoading] = useState(false);
  useEffect(() => {
    const reset = () => setGoogleLoading(false);
    window.addEventListener('pageshow', reset);
    return () => window.removeEventListener('pageshow', reset);
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setLoading(true);
    try {
      if (pending) {
        const v = await verify2fa(pending, otp, merken);
        navigate(ziel(v?.user || v), { replace: true });
        return;
      }
      const r = await login(form.email, form.password);
      if (r?.two_factor_required) { setPending(r.pending_token); return; }
      navigate(ziel(r?.user || r), { replace: true });
    } catch (err) {
      const d = err.response?.data?.detail;
      const st = err.response?.status;
      setError(!st || st >= 500 ? 'SERVER_NICHT_ERREICHBAR' : d === 'EMAIL_NICHT_BESTAETIGT' ? 'Bitte bestätigen Sie zuerst Ihre E-Mail-Adresse – wir haben Ihnen einen Bestätigungslink geschickt. Alternativ können Sie sich per Anmeldelink einloggen.' : (typeof d === 'string' ? d : t('login.error_default')));
    } finally {
      setLoading(false);
    }
  };

  const handleGoogle = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    // Preserve the ?next= path through OAuth roundtrip via sessionStorage
    if (nextParam) {
      try { sessionStorage.setItem('post_login_next', nextParam); } catch (err) { console.warn('[login] sessionStorage nicht verfügbar', err); }
    }
    setGoogleLoading(true);
    const redirectUrl = window.location.origin + '/auth/callback';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const fieldClass =
    'w-full pl-11 pr-4 py-3 bg-white border border-gray-200 focus:border-[#b8893d] focus:outline-none focus:ring-2 focus:ring-[#b8893d]/20 text-sm rounded-sm';

  return (
    <div className="min-h-screen bg-white">
      <Header />
      <main className="pt-[160px] pb-20 bg-[#f5f8fa] min-h-[80vh]">
        <div className="max-w-md mx-auto px-6">
          <div className="bg-white p-8 lg:p-10 rounded-sm shadow-sm border-l-4 border-[#b8893d]">
            <div className="text-center mb-8">
              <img src="/assets/emblem.png" alt="" aria-hidden="true" className="h-20 w-auto mx-auto mb-4 select-none" draggable="false" data-testid="auth-emblem" />
              <div className="inline-flex items-center gap-2 text-[#b8893d] text-xs tracking-[0.3em] mb-3">
                <span className="w-10 h-px bg-[#b8893d]"></span>
                {t('login.header_label')}
                <span className="w-10 h-px bg-[#b8893d]"></span>
              </div>
              <h1 className="font-serif text-3xl text-[#1B3A5C]">{t('login.heading')}</h1>
              <p className="text-[#34536b] text-sm mt-2">{t('login.subheading')}</p>
            </div>

            <UpdateHinweis />
            {error && (
              <div data-testid="login-error" className="flex items-start gap-2 p-3 mb-5 bg-rose-50 border-l-4 border-rose-500 text-rose-700 text-sm">
                <AlertCircle className="w-4 h-4 flex-shrink-0 mt-0.5" />
                {error === 'SERVER_NICHT_ERREICHBAR' || /cloudflare|origin web server/i.test(error) ? (
                  <span data-testid="login-error-server">Die Anmeldung ist gerade vorübergehend nicht möglich – unser Server antwortet nicht. Bitte versuchen Sie es in wenigen Minuten erneut oder{' '}
                    <button type="button" onClick={() => { const el = document.getElementById('login-magic-link'); el?.scrollIntoView({ behavior: 'smooth', block: 'center' }); el?.querySelector('input')?.focus(); }} className="inline text-left underline font-medium hover:text-[#b8893d]" data-testid="login-error-magic-link">lassen Sie sich einen Anmeldelink per E-Mail schicken</button></span>
                ) : <span>{error}</span>}
              </div>
            )}

            <form onSubmit={handleSubmit} className="space-y-4">
              {pending && (
                <div className="space-y-2" data-testid="login-2fa-step">
                  <p className="text-sm text-[#1B3A5C]">{t('konto.zweifa_login_hinweis')}</p>
                  <input value={otp} onChange={(e) => setOtp(e.target.value.replace(/\D/g, '').slice(0, 6))} inputMode="numeric" autoFocus required
                    placeholder="123456" className="w-full px-4 py-3 border border-gray-200 font-mono tracking-[0.4em] text-center text-lg focus:border-[#b8893d] focus:outline-none" data-testid="login-2fa-code" />
                  <label className="flex items-center gap-2 text-sm text-[#1B3A5C] cursor-pointer select-none">
                    <input type="checkbox" checked={merken} onChange={(e) => setMerken(e.target.checked)} className="w-4 h-4 accent-[#1B3A5C]" data-testid="login-2fa-merken" />
                    Dieses Gerät für heute merken (bis Mitternacht kein Code)
                  </label>
                </div>
              )}
              <div className={pending ? 'hidden' : 'relative'}>
                <Mail className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#34536b]" />
                <input data-testid="login-email-input"
                  type="email"
                  required={!pending}
                  disabled={!!pending}
                  placeholder={t('login.email_placeholder')}
                  value={form.email}
                  onChange={(e) => setForm({ ...form, email: e.target.value })}
                  className={fieldClass}
                />
              </div>
              <div className={pending ? 'hidden' : 'relative'}>
                <Lock className="absolute left-4 top-1/2 -translate-y-1/2 w-4 h-4 text-[#34536b]" />
                <input data-testid="login-password-input"
                  type={showPw ? 'text' : 'password'}
                  required={!pending}
                  disabled={!!pending}
                  placeholder={t('login.password_placeholder')}
                  value={form.password}
                  onChange={(e) => setForm({ ...form, password: e.target.value })}
                  className={`${fieldClass} pr-11`}
                />
                <button
                  type="button"
                  onClick={() => setShowPw(!showPw)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-[#34536b] hover:text-[#b8893d]"
                  aria-label={t('login.password_toggle_aria')}
                >
                  {showPw ? <EyeOff className="w-4 h-4" /> : <Eye className="w-4 h-4" />}
                </button>
              </div>

              <Button data-testid="login-submit-button"
                type="submit"
                disabled={loading}
                className="w-full bg-[#1B3A5C] hover:bg-[#0f2740] text-white tracking-[0.15em] text-xs py-6"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : (
                  <>
                    <LogIn className="mr-2 w-4 h-4" /> {t('login.submit_button')}
                  </>
                )}
              </Button>
              <div className="text-center text-xs">
                <Link to="/passwort-vergessen" data-testid="link-forgot-pw" className="text-[#34536b] hover:text-[#b8893d] tracking-wider">
                  {t('login.forgot_password_link')}
                </Link>
              </div>
            </form>

            <div className="flex items-center gap-3 my-6">
              <div className="flex-1 h-px bg-gray-200"></div>
              <span className="text-xs text-[#34536b]/60 tracking-wider">{t('login.divider_or')}</span>
              <div className="flex-1 h-px bg-gray-200"></div>
            </div>
            {!pending && <div className="mb-4" id="login-magic-link"><MagicLinkAnfordern /></div>}

            <button
              type="button"
              onClick={handleGoogle}
              disabled={googleLoading}
              data-testid="login-google-btn"
              className="w-full flex items-center justify-center gap-3 py-3 bg-white border-2 border-gray-200 hover:border-[#b8893d] rounded-sm text-sm font-medium text-[#1B3A5C] transition-colors"
            >
              <svg className="w-5 h-5" viewBox="0 0 24 24">
                <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
                <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
                <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
                <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
              </svg>
              {t('login.google_button')}
              {googleLoading && <Loader2 className="w-4 h-4 animate-spin text-[#b8893d]" />}
            </button>

            <div className="mt-8 text-center text-sm text-[#34536b]">
              {t('login.no_account')}{' '}
              <Link to="/register" className="text-[#b8893d] hover:underline font-medium">
                {t('login.register_link')}
              </Link>
            </div>
          </div>

          <p className="text-center text-xs text-[#34536b]/60 mt-6 px-4">
            {t('login.security_notice')}
          </p>
        </div>
      </main>
      <Footer />
    </div>
  );
};

export default Login;
